import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Icon from "../AppIcon";
import Button from "./Button";
import { useAuth } from "../../contexts/AuthContext";
import ConnectionStatus from "./ConnectionStatus";

const Header = ({ className = "", calculationData = null, onExportPDF }) => {
  const navigate = useNavigate();
  const { user, userProfile, signOut } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showMobileMenu, setShowMobileMenu] = useState(false);

  const handleSignOut = async () => {
    setShowUserMenu(false);
    setShowMobileMenu(false);
    try {
      await signOut();
      navigate("/");
    } catch (error) {
      console.error("Sign out error:", error);
      navigate("/");
    }
  };

  const navigationItems = [
    {
      label: "Tools",
      children: [
        { label: "Rent Calculator", path: "/", icon: "Calculator" },
        {
          label: "Agreement Generator",
          path: "/land-rental-agreement-template-generator",
          icon: "FileText",
        },
        {
          label: "Weather Risk Assessment",
          path: "/weather-risk-assessment-dashboard",
          icon: "CloudRain",
        },
        {
          label: "Insurance Calculator",
          path: "/insurance-premium-calculator",
          icon: "Shield",
        },
        {
          label: "GPS Field Mapping",
          path: "/gps-field-mapping-interface",
          icon: "Map",
        },
        {
          label: "AI Scenario Simulator",
          path: "/ai-scenario-simulator-dashboard",
          icon: "Brain",
        },
        {
          label: "AI Agriculture Assistant",
          path: "/ai-agricultural-assistant-chat-v4",
          icon: "MessageSquare",
        },
      ],
    },
    {
      label: "Analytics",
      children: [
        {
          label: "Results Dashboard",
          path: "/results-dashboard",
          icon: "BarChart3",
        },
        {
          label: "Market Alerts",
          path: "/market-trend-alerts-dashboard",
          icon: "TrendingUp",
        },
        {
          label: "Market Rates",
          path: "/agricultural-land-rental-rates-2025",
          icon: "DollarSign",
        },
      ],
    },
    {
      label: "Resources",
      children: [
        { label: "Blog", path: "/blog", icon: "BookOpen" },
        { label: "Grants & Funding", path: "/grants", icon: "DollarSign" },
        {
          label: "Legal Guide",
          path: "/guides/farmland-leasing",
          icon: "Scale",
        },
        { label: "Insights Hub", path: "/insights-hub", icon: "Lightbulb" },
        {
          label: "Professional Directory",
          path: "/professional-resources-directory",
          icon: "Users",
        },
      ],
    },
    {
      label: "Account",
      children: [
        {
          label: "Pricing Plans",
          path: "/pricing-plans-page",
          icon: "CreditCard",
        },
        {
          label: "Subscription Management",
          path: "/subscription-management-portal",
          icon: "Settings",
        },
        {
          label: "Enterprise API",
          path: "/enterprise-api-management-portal",
          icon: "Code",
        },
      ],
    },
  ];

  const NavDropdown = ({ item, mobile = false }) => {
    const [isOpen, setIsOpen] = useState(false);
    const closeTimeoutRef = useRef(null);

    const handleMouseEnter = () => {
      if (!mobile) {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
          closeTimeoutRef.current = null;
        }
        setIsOpen(true);
      }
    };

    const handleMouseLeave = () => {
      if (!mobile) {
        closeTimeoutRef.current = setTimeout(() => {
          setIsOpen(false);
        }, 150); // Small delay to allow cursor to move to dropdown
      }
    };

    useEffect(() => {
      return () => {
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }
      };
    }, []);

    return (
      <div
        className={`relative ${mobile ? "w-full" : ""}`}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <button
          onClick={() => setIsOpen(!isOpen)}
          className={`flex items-center space-x-1 text-muted-foreground hover:text-foreground transition-colors ${
            mobile ? "w-full justify-between p-3" : ""
          }`}
        >
          <span>{item?.label}</span>
          <Icon name={isOpen ? "ChevronUp" : "ChevronDown"} size={16} />
        </button>

        {isOpen && (
          <div
            className={`${
              mobile
                ? "w-full bg-muted/50 rounded-lg mt-2"
                : "absolute top-full left-0 pt-1 w-64 z-50"
            }`}
          >
            <div
              className={`bg-card rounded-lg ${
                mobile ? "" : "border border-border"
              }`}
            >
              <div className={mobile ? "p-2" : "py-2"}>
                {item?.children?.map((child) => (
                  <button
                    key={child?.path}
                    onClick={() => {
                      navigate(child?.path);
                      setIsOpen(false);
                      if (mobile) setShowMobileMenu(false);
                    }}
                    className={`flex items-center space-x-3 text-left transition-colors ${
                      mobile
                        ? "w-full p-3 hover:bg-muted rounded-md"
                        : "w-full px-4 py-3 hover:bg-muted"
                    }`}
                  >
                    <Icon
                      name={child?.icon}
                      size={16}
                      className="text-primary"
                    />
                    <div>
                      <div className="font-medium text-card-foreground">
                        {child?.label}
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <ConnectionStatus />
      <header
        className={`bg-white shadow-sm border-b border-gray-200 ${className}`}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <div
              className="flex items-center space-x-3 cursor-pointer"
              onClick={() => navigate("/")}
            >
              <div className="flex items-center justify-center w-8 h-8 bg-primary rounded-lg">
                <Icon
                  name="Wheat"
                  size={16}
                  color="var(--color-primary-foreground)"
                />
              </div>
              <div>
                <span className="text-lg font-bold text-foreground">
                  AgRentIndex.com
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8">
              {navigationItems?.map((item) => (
                <NavDropdown key={item?.label} item={item} />
              ))}
            </nav>

            {/* Actions */}
            <div className="flex items-center space-x-4">
              {/* Export PDF Button */}
              {calculationData && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={onExportPDF}
                  iconName="Download"
                  iconPosition="left"
                  className="hidden sm:flex"
                >
                  Export PDF
                </Button>
              )}

              {/* User Menu */}
              {user ? (
                <div className="relative">
                  <button
                    onClick={() => setShowUserMenu(!showUserMenu)}
                    className="flex items-center space-x-2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-sm font-medium text-primary-foreground">
                        {userProfile?.full_name?.charAt(0)?.toUpperCase() ||
                          user?.email?.charAt(0)?.toUpperCase()}
                      </span>
                    </div>
                    <Icon name="ChevronDown" size={16} />
                  </button>

                  {showUserMenu && (
                    <>
                      <div className="absolute right-0 mt-2 w-64 bg-card border border-border rounded-lg shadow-lg py-2 z-50">
                        {/* User Info */}
                        <div className="px-4 py-3 border-b border-border">
                          <p className="text-sm font-medium text-card-foreground">
                            {userProfile?.full_name || "User"}
                          </p>
                          <p className="text-xs text-muted-foreground">
                            {user?.email}
                          </p>
                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary capitalize">
                              {userProfile?.subscription_tier === "consultant"
                                ? "Advisor"
                                : userProfile?.subscription_tier ===
                                  "enterprise"
                                ? "Enterprise"
                                : userProfile?.subscription_tier === "pro"
                                ? "Pro"
                                : "Free"}{" "}
                              Plan
                            </span>
                          </div>
                        </div>

                        {/* Report Usage */}
                        <div className="px-4 py-3 border-b border-border">
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground">
                              Reports Used:
                            </span>
                            <span className="font-medium">
                              {userProfile?.reports_used || 0} /{" "}
                              {userProfile?.max_reports === 999999
                                ? "∞"
                                : userProfile?.max_reports || 1}
                            </span>
                          </div>
                          <div className="mt-2">
                            <div className="bg-muted rounded-full h-2">
                              <div
                                className="bg-primary rounded-full h-2 transition-all"
                                style={{
                                  width:
                                    userProfile?.max_reports === -1
                                      ? "100%"
                                      : `${Math.min(
                                          100,
                                          ((userProfile?.reports_used || 0) /
                                            (userProfile?.max_reports || 1)) *
                                            100
                                        )}%`,
                                }}
                              />
                            </div>
                          </div>
                        </div>

                        {/* Menu Items */}
                        <button
                          onClick={() => {
                            navigate("/results-dashboard");
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-card-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                        >
                          <Icon name="BarChart3" size={16} />
                          <span>Dashboard</span>
                        </button>

                        <button
                          onClick={() => {
                            navigate("/subscription-management-portal");
                            setShowUserMenu(false);
                          }}
                          className="w-full px-4 py-2 text-left text-sm text-card-foreground hover:bg-muted transition-colors flex items-center space-x-2"
                        >
                          <Icon name="CreditCard" size={16} />
                          <span>Subscription</span>
                        </button>

                        <div className="border-t border-border mt-2 pt-2">
                          <button
                            type="button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleSignOut();
                            }}
                            className="w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 transition-colors flex items-center space-x-2"
                          >
                            <Icon name="LogOut" size={16} />
                            <span>Sign Out</span>
                          </button>
                        </div>
                      </div>
                      <div
                        className="fixed inset-0 z-40"
                        onClick={() => setShowUserMenu(false)}
                      />
                    </>
                  )}
                </div>
              ) : (
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate("/sign-in")}
                  >
                    Sign In
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => navigate("/sign-up")}
                    className="bg-primary text-primary-foreground hover:bg-primary/90"
                  >
                    Sign Up
                  </Button>
                </div>
              )}

              {/* Mobile Menu Button */}
              <button
                className="lg:hidden text-muted-foreground hover:text-foreground"
                onClick={() => setShowMobileMenu(!showMobileMenu)}
              >
                <Icon name={showMobileMenu ? "X" : "Menu"} size={20} />
              </button>
            </div>
          </div>

          {/* Mobile Navigation */}
          {showMobileMenu && (
            <div className="lg:hidden border-t border-border">
              <div className="px-4 py-6 space-y-4">
                {navigationItems?.map((item) => (
                  <NavDropdown key={item?.label} item={item} mobile={true} />
                ))}

                {/* Mobile Auth Actions */}
                {!user && (
                  <div className="pt-4 border-t border-border space-y-3">
                    <Button
                      variant="outline"
                      onClick={() => {
                        navigate("/sign-in");
                        setShowMobileMenu(false);
                      }}
                      className="w-full"
                    >
                      Sign In
                    </Button>
                    <Button
                      onClick={() => {
                        navigate("/sign-up");
                        setShowMobileMenu(false);
                      }}
                      className="w-full"
                    >
                      Sign Up
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </header>
    </>
  );
};

export default Header;
